import React, { useState, useMemo, useCallback } from "react";
import { TreeSelect } from "antd";

interface CategoryFilterProps {
  assetCategoryOptions: any[];
  categoryFilter?: any;
  setCategoryFilter?: any;
  categoryBreadcrumbPath?: any;
  setCategoryBreadcrumbPath?: any;
  onClearFilter?: any;
}

interface CategoryNode {
  id: string;
  name: string;
  status: string;
  category_level: number;
  parentId?: string | null;
  subcategories?: CategoryNode[];
}

const CategoryFilter: React.FC<CategoryFilterProps> = ({
  assetCategoryOptions,
  categoryFilter,
  setCategoryFilter,
  categoryBreadcrumbPath,
  setCategoryBreadcrumbPath,
  onClearFilter,
}) => {
  const generateCategoryBreadcrumb = useCallback(
    (
      categoryId: string,
      categories: CategoryNode[] = assetCategoryOptions
    ): string => {
      const findCategoryPathRecursive = (
        cats: CategoryNode[],
        targetId: string,
        currentPath: string[] = []
      ): string[] | null => {
        for (const cat of cats) {
          const newPath = [...currentPath, cat.name];

          if (cat.id === targetId) {
            return newPath;
          }

          if (cat.subcategories && cat.subcategories.length > 0) {
            const foundPath = findCategoryPathRecursive(
              cat.subcategories,
              targetId,
              newPath
            );
            if (foundPath) {
              return foundPath;
            }
          }
        }
        return null;
      };

      const pathArray = findCategoryPathRecursive(categories, categoryId);
      return pathArray ? pathArray.join(" → ") : "";
    },
    [assetCategoryOptions]
  );

  const handleCategorySelectionChange = (categoryValue: string) => {
    setCategoryFilter(categoryValue);
    const breadcrumbPath = generateCategoryBreadcrumb(categoryValue);
    setCategoryBreadcrumbPath(breadcrumbPath);
  };

  const categoryTreeData = useMemo(() => {
    const convertCategoriesToTreeFormat = (
      categories: CategoryNode[]
    ): any[] => {
      if (!categories || !Array.isArray(categories)) {
        return [];
      }

      return categories
        .filter((category) => category && category.status === "Active")
        .map((category) => {
          const hasChildCategories =
            category.subcategories && category.subcategories.length > 0;

          return {
            title: category.name,
            value: category.id,
            key: category.id,
            children: hasChildCategories
              ? convertCategoriesToTreeFormat(category.subcategories!)
              : undefined,
          };
        });
    };

    return convertCategoriesToTreeFormat(assetCategoryOptions || []);
  }, [assetCategoryOptions]);

  return (
    <>
      {/* Category Filter */}
      <div
        className="ant-tree-select-div me-2 d-flex gap-2"
        style={{ width: "100%" }}
        title="Category Filter">
        <TreeSelect
          value={categoryFilter}
          showSearch
          treeNodeFilterProp="title"
          filterTreeNode={(input, node) => {
            if (!input || !node) return false;
            const inputLower = input.toLowerCase();
            const title = node.title?.toString().toLowerCase() || "";
            return title.includes(inputLower);
          }}
          searchValue={undefined}
          autoClearSearchValue={true}
          dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
          treeData={categoryTreeData}
          placeholder="Please select a category"
          allowClear
          treeDefaultExpandAll={false}
          onChange={handleCategorySelectionChange}
          getPopupContainer={(triggerNode) =>
            triggerNode.parentElement || document.body
          }
        />
        <button
          title="Clear Select Category"
          className="btn btn-sm btn-info"
          onClick={onClearFilter}
          disabled={!categoryFilter}>
          Clear
        </button>
      </div>

      <div className="mt-1 text-center" style={{ minHeight: "42px" }}>
        {categoryBreadcrumbPath ? (
          <small className="text-muted">
            <i className="ti ti-route me-1"></i>
            Selected: <strong>{categoryBreadcrumbPath}</strong>
          </small>
        ) : (
          <small className="text-primary">
            Leave empty to show all assets stats
          </small>
        )}
      </div>
    </>
  );
};

export default CategoryFilter;
